import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { kebabCase } from 'lodash';
import { cloneDeep } from 'lodash/fp';
import { ReactNode, useState } from 'react';
import { DragDropContext, Droppable, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import { ValidateResult } from 'react-hook-form';
import styled from 'styled-components';

import DraggableListItem from 'dashboard/components/DraggableListItem';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import FormNewGridLayoutTab from 'dashboard/forms/FormNewGridLayoutTab';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { reorder } from 'dashboard/lib/dnd/utils';
import { BaseModule } from 'dashboard/lib/module';
import { EnvironmentName } from 'dashboard/models/environment';
import { Tab } from 'dashboard/models/grid-layout';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import useConfirmation from 'lib/hooks/useConfirmation';
import useToggle from 'lib/hooks/useToggle';

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  padding: ${Spacing[5]};
`;

const TabListHeaderContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${({ theme }) => theme.element.borderRadius};

  padding: ${Spacing[3]};
`;

export interface TabsProps<T> {
  tabs: T[];
  environmentName: EnvironmentName;
  addTab: (name: string) => void;
  updateTabs: (tabs: T[]) => void;
  renderTabForm: (
    tab: T,
    handleHideModal: () => void,
    handleEdit: (tab: T) => void,
    handleValidateTabName: (tabName: string, tabToken?: string) => ValidateResult,
  ) => ReactNode;
}

function Tabs<M extends BaseModule, T extends Tab<M>>({
  tabs,
  environmentName,
  addTab,
  updateTabs,
  renderTabForm,
}: TabsProps<T>) {
  const [selectedTabToken, setSelectedTabToken] = useState<string | undefined>(undefined);
  const selectedTab = tabs.find((t) => t.token === selectedTabToken);
  const [editNameModal, { enable: showEditNameModal, disable: hideEditModal }] = useToggle(false);
  const [showDeleteTabModal, { prompt, confirm, cancel }] = useConfirmation();

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    updateTabs(reorder(tabs, source.index, destination.index));
  };

  const handleDelete = (tabToken: string) => updateTabs(tabs.filter((t) => t.token !== tabToken));
  const handleEdit = (tab: T) => {
    const nextTabs = cloneDeep(tabs);
    const tabIndex = nextTabs.findIndex((t) => t.token === selectedTabToken);

    nextTabs[tabIndex] = tab;

    updateTabs(nextTabs);
    hideEditModal();
  };

  const handleValidateTabName = (tabName: string, tabToken?: string): ValidateResult => {
    // we want to filter the currently edited tab,
    // when creating a new tab, tabToken will be null preventing any tabs from being filtered.
    const existingTabNames = tabs
      .filter((tab) => tab.token !== tabToken)
      // tabs must be unique when kebab-cased so that tab routes are always unique.
      .map((tab) => kebabCase(tab.name));

    if (existingTabNames.includes(kebabCase(tabName))) {
      return 'Tab names can not be too similar';
    } else {
      return true;
    }
  };

  // Generate DnDs for each tab
  //  - include delete and update
  const tabsDnd = tabs.map((tab, idx) => {
    const handleEditNameModal = () => {
      setSelectedTabToken(tab.token);
      showEditNameModal();
    };
    const handleDeleteTabModal = () => prompt(() => handleDelete(tab.token));

    const actionItems = [
      {
        id: 'control-panel-tab__edit-tab-name',
        name: 'control-panel-tab__edit-tab-name',
        'data-test': 'control-panel-tab__edit-tab-name',
        onClick: handleEditNameModal,
        disabled: false,
        label: 'Edit',
        variant: 'basic' as const,
      },
      {
        id: 'control-panel-tab__delete-tab-name',
        name: 'control-panel-tab__delete-tab-name',
        'data-test': 'control-panel-tab__delete-tab-name',
        onClick: handleDeleteTabModal,
        disabled: tabs.length === 1,
        label: 'Delete',
        variant: 'danger' as const,
      },
    ];

    const key = `${tab.token} - ${idx}`;

    return (
      <>
        <DraggableListItem
          key={key}
          draggableId={key}
          index={idx}
          disableDrag={false}
          dataTest={`control-panel-tab__draggable-item--${tab.token}`}
          suffix={
            <DropdownMenu
              trigger={
                <Button
                  id={`control-panel-tab__action-button--${tab.token}`}
                  tabIndex={0}
                  data-test={`control-panel-tab__action-button--${tab.token}`}
                  variant='basic'
                  width='icon'
                  type='button'
                >
                  <MoreIcon />
                </Button>
              }
              align='end'
            >
              {actionItems.map((item) => (
                <DropdownMenu.Item
                  data-test={item['data-test']}
                  id={item.id}
                  variant={item.variant}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    item.onClick();
                  }}
                  disabled={item.disabled}
                  key={item.id}
                >
                  {item.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          }
        >
          <Paragraph>{tab.name}</Paragraph>
        </DraggableListItem>
      </>
    );
  });

  return (
    <TabsContainer>
      <Section
        id={`component-library__prebuilt-modules--tabs--${environmentName}`}
        data-test={`component-library__prebuilt-modules--tabs--${environmentName}`}
      >
        <Heading size='sm'>Tabs</Heading>
        <Spacer height={2} />
        <TabListHeaderContainer>
          <Label size='md' weight='bold'>
            Tabs
          </Label>
        </TabListHeaderContainer>
        <div>
          {tabsDnd.length > 0 && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId='case-layout-checklist-module-edit-modal__droppable-selected'>
                {(provided) => {
                  return (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {tabsDnd}
                      {provided.placeholder}
                    </div>
                  );
                }}
              </Droppable>
            </DragDropContext>
          )}
        </div>
        <Spacer height={2} />
        <FormNewGridLayoutTab
          submit={(data) => addTab(data.tabName)}
          validateTabName={handleValidateTabName}
        />
      </Section>
      {selectedTab && (
        <ModalWrapper isOpen={editNameModal} handleCloseModal={hideEditModal}>
          <ModalContainer data-test='edit-name-modal'>
            <ModalHeader onClose={hideEditModal}>Edit tab</ModalHeader>
            <ModalBody>
              {renderTabForm(selectedTab, hideEditModal, handleEdit, handleValidateTabName)}
            </ModalBody>
          </ModalContainer>
        </ModalWrapper>
      )}
      <ModalWrapper isOpen={showDeleteTabModal} handleCloseModal={cancel}>
        <ConfirmationModal
          cancelAction={cancel}
          confirmAction={confirm}
          header='Delete this tab?'
          confirmActionText='Delete'
        >
          This tab and all of its configurations will be deleted.
        </ConfirmationModal>
      </ModalWrapper>
    </TabsContainer>
  );
}

export default Tabs;
